import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { defineStore } from 'pinia';

import { setAuthState } from '../composables/auth';
import { update } from '../api/profile.js';
import { trackNudge } from '../api/nudge.js';
import { useUserEvents, USER_EVENTS } from './user-events.store';

/**
 * Coordinates the two first-run "first application" retention moments:
 * - EN-638 exit-intent modal (before the first application is submitted)
 * - EN-639 paywall modal (immediately after the first application is submitted)
 *
 * The window is scoped to a net-new free user who arrived from the Inertia onboarding
 * handoff (`/job-board/search?firstRun=1`) and has not yet submitted their first
 * application. Milestone flags are persisted on `user.options` via the existing
 * profile update API (the same pattern as `markFirstRunSeen` in the job-board store),
 * so the window state and "shown once" guarantees survive reloads and return visits.
 */
export const useOnboardingRetentionStore = defineStore('onboarding-retention', () => {
	const { getUser, setUser } = setAuthState();
	const router = useRouter();
	const userEvents = useUserEvents();

	const user = getUser();

	// Persisted milestone flags (mirrored from user.options, kept in sync on write).
	const firstRunActive = ref(!!user?.options?.onboarding_first_run_active);
	const firstApplicationSubmitted = ref(!!user?.options?.first_application_submitted);
	const paywallSeen = ref(!!user?.options?.onboarding_paywall_seen);

	// Session-only: EN-638 must not reappear after "Leave Anyway" once per session.
	const exitIntentDismissedThisSession = ref(false);

	// Modal / navigation UI state.
	const showExitIntentModal = ref(false);
	const showPaywallModal = ref(false);
	const showCoachingModal = ref(false);
	const pendingNavigation = ref(null);

	const isNetNewFreeUser = () => !getUser()?.current_plan;

	// EN-1185: a sent Job Tracker application (the count Your Journey's Application stage reads)
	// closes the window however it was sent: external apply, Job Tracker, email sync, or before
	// the window opened. The flag above is set only by AutoApply submits on the job board.
	const hasSentApplication = () => !!getUser()?.has_sent_application;

	/**
	 * A function, not a computed: the router guard first asks before App.vue refreshes the cached
	 * user from /api/user, and a computed would keep that first answer because the cache isn't reactive.
	 */
	const isExitIntentWindowOpen = () => firstRunActive.value
		&& !firstApplicationSubmitted.value
		&& !hasSentApplication()
		&& isNetNewFreeUser();

	const persistOptions = async (patch) => {
		const response = await update({
			options: {
				...getUser()?.options,
				...patch,
			},
		});
		setUser(response.data.data);
	};

	/**
	 * Open the retention window for a net-new free user landing from the onboarding
	 * handoff. Idempotent and a no-op for paid users or users who already applied.
	 */
	const activateFirstRunWindow = async () => {
		if (firstRunActive.value || firstApplicationSubmitted.value || !isNetNewFreeUser()) {
			return;
		}

		firstRunActive.value = true;
		try {
			await persistOptions({ onboarding_first_run_active: true });
		} catch {
			// Best-effort: the in-memory flag still drives the window this session.
		}
	};

	const requestExitIntent = (toFullPath) => {
		pendingNavigation.value = toFullPath ?? null;
		showExitIntentModal.value = true;
		userEvents.trackEvent(USER_EVENTS.EXIT_INTENT_SHOWN);
	};

	/** "Finish My First Application" — dismiss and stay; no state reset. */
	const cancelStay = () => {
		showExitIntentModal.value = false;
		pendingNavigation.value = null;
		userEvents.trackEvent(USER_EVENTS.EXIT_INTENT_STAY);
	};

	/** "Leave Anyway" — dismiss, suppress for the session, and proceed. */
	const confirmLeave = () => {
		exitIntentDismissedThisSession.value = true;
		showExitIntentModal.value = false;
		const destination = pendingNavigation.value;
		pendingNavigation.value = null;
		userEvents.trackEvent(USER_EVENTS.EXIT_INTENT_LEAVE);
		if (destination) {
			router.push(destination);
		}
	};

	/**
	 * Record the first successful application (internal AutoApply or external apply).
	 * Closes the exit-intent window and, for an eligible net-new free user, surfaces
	 * the paywall exactly once. Only acts for users inside the first-run window.
	 */
	const recordFirstApplicationSubmitted = async () => {
		if (!firstRunActive.value || firstApplicationSubmitted.value) {
			return;
		}

		firstApplicationSubmitted.value = true;
		showExitIntentModal.value = false;
		pendingNavigation.value = null;

		if (isNetNewFreeUser() && !paywallSeen.value) {
			showPaywallModal.value = true;
		}

		try {
			await persistOptions({ first_application_submitted: true });
		} catch {
			// Best-effort: the in-memory flag still closes the window this session.
		}
	};

	/** Paywall dismissed via the close button or after "Upgrade Plan". */
	const markPaywallSeen = async () => {
		paywallSeen.value = true;
		showPaywallModal.value = false;
		try {
			await persistOptions({ onboarding_paywall_seen: true });
		} catch {
			// Best-effort.
		}
	};

	/** Surface the coaching nudge modal and record the impression. */
	const openCoachingModal = () => {
		showCoachingModal.value = true;
		trackNudge({ feature: 'auto_apply', event: 'shown' });
	};

	/**
	 * "Stay On Free" on the paywall: the user declined the upgrade, so offer a free
	 * coaching call instead. Surfaces the coaching nudge, records the impression, and
	 * marks the paywall seen. Reachable only through the once-only paywall, so the
	 * coaching modal is inherently shown at most once — no separate persisted flag.
	 */
	const showCoachingAfterStayFree = async () => {
		openCoachingModal();
		await markPaywallSeen();
	};

	/** Coaching nudge dismissed via "Maybe Later", its close button, or after booking. */
	const dismissCoachingModal = () => {
		showCoachingModal.value = false;
	};

	return {
		firstRunActive,
		firstApplicationSubmitted,
		paywallSeen,
		exitIntentDismissedThisSession,
		showExitIntentModal,
		showPaywallModal,
		showCoachingModal,
		pendingNavigation,
		isExitIntentWindowOpen,

		activateFirstRunWindow,
		requestExitIntent,
		cancelStay,
		confirmLeave,
		recordFirstApplicationSubmitted,
		markPaywallSeen,
		openCoachingModal,
		showCoachingAfterStayFree,
		dismissCoachingModal,
	};
});
